import { logError } from '../../services/errorHandler'

const ALLOWED_PROTOCOLS = new Set(['https:', 'http:'])

// Module-level dedup. The helper is called during render flows (badge
// rendering, chooser fallback timer), so without this an env misconfig
// would fire logError on every render — flooding Sentry with the same
// event hundreds of times per session. Key by reason + offending value
// so distinct bad inputs are still reported, but each one only once.
const loggedInvalidUrlKeys = new Set<string>()

const logInvalidUrlOnce = (
    reason: string,
    dedupValue: string,
    context: Record<string, unknown>,
): void => {
    const key = `${reason}:${dedupValue}`
    if (loggedInvalidUrlKeys.has(key)) return
    loggedInvalidUrlKeys.add(key)
    logError(new Error(`toSafeExternalUrl: ${reason}`), context)
}

/**
 * Validate that a string is a parseable URL with an allow-listed protocol,
 * returning the canonical URL string or `null` if invalid.
 *
 * Why: the app-store / play-store badges (and the chooser's fallback redirect)
 * take their URLs from build-time env vars. Today nothing user-controlled
 * reaches `href` or `window.location.href` here, but a previous version
 * relied on `DOMPurify.sanitize()` for safety — which is a no-op on plain
 * strings and offered no real protection. This helper makes the contract
 * explicit: anything that isn't http(s) becomes `null`, callers render a
 * fallback rather than ship a `javascript:` / `data:` payload to `href`.
 *
 * On invalid input, log to Sentry once per unique bad value — the only way
 * an invalid value gets here is an env misconfiguration we want to see in
 * production telemetry, but a single misconfig must not flood the
 * dashboard on every render.
 */
export const toSafeExternalUrl = (
    raw: string | undefined | null,
): string | null => {
    if (!raw) return null
    try {
        const parsed = new URL(raw)
        if (!ALLOWED_PROTOCOLS.has(parsed.protocol)) {
            logInvalidUrlOnce('disallowed protocol', raw, {
                protocol: parsed.protocol,
                raw,
            })
            return null
        }
        return parsed.toString()
    } catch {
        logInvalidUrlOnce('unparseable URL', raw, { raw })
        return null
    }
}
